import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { useAuth } from "./auth/useAuth";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import AppLayout from "./layout/AppLayout";
import { lazy, Suspense, useEffect, useState } from "react";
import { Button, Snackbar, IconButton } from "@mui/material";
import { useNoteSyncBootstrap } from "./offline/noteSyncStore";
import { useDownloadBootstrap } from "./offline/useDownloadBootstrap";
import ToastHost from "./ui/ToastHost";
import LoadingSplash from "./components/LoadingSplash";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocationPermissionDialog from "./location/LocationPermissionDialog";
import { useLocationBootstrap } from "./location/useLocationBootstrap";

const Login = lazy(() => import("./pages/Login"));
const Search = lazy(() => import("./pages/Search"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const AddressDetailWithLayout = lazy(() => import("./pages/AddressDetailWithLayout"));
const NoteMap = lazy(() => import("./pages/NoteMap"));
const ReportsHome = lazy(() => import("./pages/ReportsHome"));
const ReportsStatus = lazy(() => import("./pages/ReportsStatus"));
const ReportsAgent = lazy(() => import("./pages/ReportsAgent"));
const ReportsCoverageClusters = lazy(() => import("./pages/ReportsCoverageClusters"));
const ReportsCoverageHeatmap = lazy(() => import("./pages/ReportsCoverageHeatmap"));
const ReportsCoverage = lazy(() => import("./pages/ReportsCoverage"));
const ReportsCoverageDeck = lazy(() => import("./pages/ReportsCoverageDeck"));
const SyncManager = lazy(() => import("./pages/SyncManager"));
const OfflineManager = lazy(() => import("./pages/OfflineManager"));

function AutoRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSplash />;
  return user ? <Navigate to="/search" /> : <Navigate to="/login" />;
}

// function ScrollToTop() {
//   const { pathname } = useLocation();
//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: "instant" });
//   }, [pathname]);
//   return null;
// }

// maintain older url for a minute, but can delete this later
function RedirectAddress() {
  const { udprn } = useParams();
  return <Navigate to={`/search/address/${udprn}`} replace />;
}

function HeaderBack({ fallback }: { fallback: string }) {
  const navigate = useNavigate();
  return (
    <IconButton
      color="inherit"
      onClick={() => {
        if (window.history.length > 2) navigate(-1);
        else navigate(fallback);
      }}
      size="small"
    >
      <ArrowBackIosNewIcon fontSize="small" />
    </IconButton>
  );
}


// Runs API-touching bootstrap hooks only when the user is authenticated.
// Must live inside <AuthProvider> so it can read useAuth().
function AuthenticatedBootstrap() {
  useNoteSyncBootstrap();
  useDownloadBootstrap();
  return null;
}

function AppBootstrap() {
  const { user } = useAuth();
  useLocationBootstrap(); // no API calls — safe to run regardless of auth
  if (!user) return null;
  return <AuthenticatedBootstrap />;
}

export default function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      setUpdateAvailable(true);
      setRegistration((e as CustomEvent<ServiceWorkerRegistration>).detail);
    };

    window.addEventListener("sw.update", handler);
    return () => window.removeEventListener("sw.update", handler);
  }, []);

  const refresh = () => {
    if (registration?.waiting) {
      // Tell the waiting SW to activate
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    window.location.reload();
  };
  return (
    <AuthProvider>
      <AppBootstrap />
      <BrowserRouter>
        <ChunkErrorBoundary>
        <Suspense fallback={null}>
          <Routes>

            {/* root redirect */}
            <Route path="/" element={<AutoRedirect />} />

            {/* login */}
            <Route path="/login" element={<Login />} />

            {/* protected */}
            <Route path="/search" element={
              <ProtectedRoute><AppLayout><Search /></AppLayout></ProtectedRoute>
            }>
              {/* nested sheet */}
              <Route path="address/:udprn" element={<AddressDetailWithLayout />} />
            </Route>

            <Route path="/address/:udprn" element={<RedirectAddress />} />

            <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsHome /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/maps" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><NoteMap /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/coverage" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverage /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/coverage-clusters" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverageClusters /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/coverage-heatmap" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverageHeatmap /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/coverage-maplibre" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverageDeck /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/status" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsStatus /></AppLayout></ProtectedRoute>} />
            <Route path="/reports/agent" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsAgent /></AppLayout></ProtectedRoute>} />
            <Route path="/note-map" element={<Navigate to="/reports/maps" replace />} />
            <Route
              path="/settings/offline"
              element={
                <ProtectedRoute>
                  <AppLayout headerAction={<HeaderBack fallback="/settings" />}>
                    <OfflineManager />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sync"
              element={
                <ProtectedRoute>
                  <AppLayout headerAction={<HeaderBack fallback="/search" />}>
                    <SyncManager />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
      <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="A new version is available"
        action={
          <Button color="secondary" size="small" onClick={refresh}>
            Refresh
          </Button>
        }
      />
      <ToastHost />
      <LocationPermissionDialog />
    </AuthProvider>
  );
}
